import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import {
  Button,
  Dialog,
  Grid,
  IconButton,
  Input,
  SnackBar,
  Typography,
} from 'components/atoms';
import { severityType } from 'components/atoms/Alert/Alert.styles';
import { Table } from 'components/organisms';
import { useDialog, useGetTable } from 'hooks';
import useTable, { filterModelType, tableModelType } from 'hooks/useTable';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IoAdd } from 'react-icons/io5';
import { PaginationType, Task } from 'types';
import { createFilterStructure } from 'utils';
import {
  defaultListRoleTemplateFilter,
  defaultPagination,
  defaultSortModel,
  fetchProcess,
  getColumns,
} from './ListTaskTemplate.const';
import {
  ListRoleTemplateFilterSchema,
  ListRoleTemplateFilterType,
} from './ListTaskTemplate.schema';
import { ListRoleTemplateProps } from './ListTaskTemplate.types';

const ListRoleTemplate = ({ ...props }: ListRoleTemplateProps) => {
  const [loadingForm, setLoadingForm] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ListRoleTemplateFilterType>({
    defaultValues: defaultListRoleTemplateFilter,
    resolver: yupResolver(ListRoleTemplateFilterSchema),
  });

  const [selected, onOpen, onCloseModal] = useDialog<number | undefined>(
    undefined,
  );
  const [action, setAction] = useState<'remove' | 'edit' | undefined>(
    undefined,
  );
  const [open, setOpen] = useState<severityType>();

  const confirmEdit = useCallback(
    (id: number) => {
      onOpen(id);
      setAction('edit');
    },
    [onOpen],
  );

  const confirmRemove = useCallback(
    (id: number) => {
      onOpen(id);
      setAction('remove');
    },
    [onOpen],
  );

  const onCloseSnackBar = useCallback(() => {
    setAction(undefined);
    setOpen(undefined);
  }, []);

  const onClose = useCallback(() => {
    onCloseModal();
  }, [onCloseModal]);

  const columns = useMemo(
    () => getColumns(confirmRemove, confirmEdit),
    [confirmEdit, confirmRemove],
  );

  const [filterModel, setFilterModel] = useState<filterModelType<Task>[]>([]);

  const {
    loading,
    error,
    data: process,
    handleData,
    getData,
    mutate,
  } = useGetTable<PaginationType<Task>, tableModelType<Task>>(fetchProcess, {
    paginationModel: defaultPagination,
    sortModel: defaultSortModel,
    filterModel,
  });

  const tableControllers = useTable<Task>(
    {
      filterModel,
      paginationModel: process
        ? { ...defaultPagination, rowsCount: process.rowsCount }
        : defaultPagination,
    },
    handleData,
    getData as (
      param: Omit<tableModelType<Task>, 'paginationModel'>,
    ) => Promise<PaginationType<Task>>,
  );

  const removeRole = async (idRole: number) => {

  };

  const editRole = async (idRole: number) => {

  };

  const onSubmit = useCallback(
    (filterListRoleTemplate: ListRoleTemplateFilterType) => {
      const filterRolename = createFilterStructure(
        'name',
        'contains',
        filterListRoleTemplate.name,
      );

      const modelFilter = [filterRolename].filter((item) => item);
      setFilterModel(modelFilter as filterModelType<Task>[]);
    },
    [],
  );

  const selectedRole = useMemo(
    () =>
      selected !== undefined
        ? process?.result.find((user) => user.id === selected)
        : undefined,
    [selected, process],
  );

  return (
    <>
      <Table<Task>
        {...tableControllers}
        numberOfVisibleColumns={4}
        title={`Encontramos ${tableControllers.paginationModel.rowsCount} Task`}
        columns={columns}
        rows={process?.result || []}
        error={!!error}
        loading={loading}
        filterComponent={
          <Grid
            container
            alignItems="center"
            justify="flex-end"
            spacing={4}
            wrap="nowrap"
          >
            <Grid item>
              <IconButton
                variant="contained"
                href="/pages/task/create"
                info="Crear task"
              >
                <IoAdd />
              </IconButton>
            </Grid>
            <form noValidate onSubmit={handleSubmit(onSubmit)}>
              <Grid item>
                <Grid
                  container
                  alignItems="center"
                  justify="flex-end"
                  spacing={4}
                  wrap="nowrap"
                >
                  <Grid item xs={10}>
                    <Input
                      fullWidth
                      placeholder="Busca por nombre"
                      {...register('name')}
                      error={!!errors.name}
                      helperText={errors.name && errors.name.message}
                    />
                  </Grid>
                  <Grid item>
                    <Button
                      isSmall
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Filtrar
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </form>
          </Grid>
        }
      />
      <Dialog
        open={selected !== undefined}
        onClose={onClose}
        title="Confirma esta acción"
      >
        <Grid container spacing={3} justify="flex-end">
          <Grid item xs={12}>
            {action === 'remove' && (
              <Typography variant="h2">
                {`¿Estas seguro de eliminar el rol ${selectedRole?.name}?`}
              </Typography>
            )}
            {action === 'edit' && (
              <Typography variant="h2">
                {`¿Estas seguro de editar el estado del rol ${selectedRole?.name}?`}
              </Typography>
            )}
          </Grid>
          <Grid item>
            <Button variant="contained" color="grey" onClick={onClose}>
              Cancelar
            </Button>
          </Grid>
          <Grid item>
            {action === 'remove' && (
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  removeRole(selectedRole?.id ? selectedRole?.id : 0)
                }
                loading={loadingForm}
              >
                Si, deseo eliminarlo
              </Button>
            )}

            {action === 'edit' && (
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  editRole(selectedRole?.id ? selectedRole?.id : 0)
                }
                loading={loadingForm}
              >
                Si, deseo modificarlo
              </Button>
            )}
          </Grid>
        </Grid>
      </Dialog>
      <SnackBar
        wait={2000}
        open={open !== undefined}
        onClose={onCloseSnackBar}
        severity={open}
      >
        {open === 'success' &&
          action === 'edit' &&
          'Se edito el estado del usuario exitosamente'}
        {open === 'success' &&
          action === 'remove' &&
          'Eliminamos el usuario exitosamente'}
        {open === 'error' && 'Ocurrió un error, intenta nuevamente'}
      </SnackBar>
    </>
  );
};

export default ListRoleTemplate;
